// src/App.tsx
import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Refine } from "@refinedev/core";
import { ConfigProvider, theme } from "antd";
import { postgrestDataProvider } from "./providers/postgrestDataProvider";
import { getToken } from "./keycloak";
import { AppLayout } from "./AppLayout";
import { GenericCrud } from "./components/GenericCrud";
import { APP_MENU } from "./menuConfig";
import { antdNotificationProvider } from "./notifications/antdNotificationProvider";
import { notistackNotificationProvider } from "./notifications/notistackNotificationProvider";
import { HelloWorld } from "./pages/HelloWorld";
import DNSControl from "./pages/DNSControl";
import WikiControl from "./pages/WikiControl";
import CAAutomationControl from "./pages/CAAutomationControl";
import CAControl from "./pages/CAControl";
import SSHCertControl from "./pages/SSHCertControl";
import IPAMControl from "./pages/IPAMControl";
import ProjectControl from "./pages/ProjectControl";
import MonitorControl from "./pages/MonitorControl";

// const API_URL = import.meta.env.VITE_API_BASE ?? "https://uapp-api.uvoo.io";

// import { envString } from "./env";
// const API_URL = envString("VITE_API_BASE", "https://uapp-api.uvoo.io")!;
// import { CONFIG } from "../config";
// fetch(`${CONFIG.API_URL}/health`);
import { VARCONFIG } from "./varconfig";

// registry for standalone pages
const PAGE_COMPONENTS: Record<string, React.ReactNode> = {
  helloWorld: <HelloWorld />,
  dnsControl: <DNSControl />,
  caAutomationControl: <CAAutomationControl />,
  caControl: <CAControl />,
  sshCertControl: <SSHCertControl />,
  ipamcontrol: <IPAMControl />,
  projectControl: <ProjectControl />,
  monitorControl: <MonitorControl />,
  wikiControl: <WikiControl />,
};

export const App: React.FC = () => {
  // theme state
  const [isDark, setIsDark] = React.useState(true);

  // refine data provider
  const dataProvider = postgrestDataProvider(
    //API_URL,
    VARCONFIG.API_URL,
    () => getToken() ?? undefined,
  );

  // flatten menu groups → routeable leaves
  const leaves = APP_MENU.flatMap((entry) =>
    entry.type === "group" ? entry.children : [entry],
  );

  React.useEffect(() => {
    document.body.classList.remove("app-dark", "app-light");
    document.body.classList.add(isDark ? "app-dark" : "app-light");
  }, [isDark]);

  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <ConfigProvider
        theme={{
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
          // algorithm: isDark ? [theme.darkAlgorithm, theme.compactAlgorithm] : [theme.defaultAlgorithm, theme.compactAlgorithm],

          token: {
            colorPrimary: "#2B4ACB", // ← YOUR BRAND COLOR
            colorInfo: "#2B4ACB",
            // borderRadius: 2,
          },

          components: {
            Layout: {
              siderBg: isDark ? "#000000" : "#ffffff",
              headerBg: isDark ? "#000000" : "#ffffff",
              bodyBg: "transparent",
            },

            Menu: isDark
              ? {
                  colorBgContainer: "#000000",

                  darkItemBg: "#000000",
                  darkSubMenuItemBg: "#000000",

                  darkItemHoverBg: "#141414",

                  // SELECTED MENU ITEM COLORS
                  darkItemSelectedBg: "#141414",
                  darkItemSelectedColor: "#2B4ACB",

                  // icon color override
                  darkItemSelectedIconColor: "#2B4ACB",
                }
              : {
                  itemHoverBg: "rgba(43, 74, 203, 0.06)",
                  itemSelectedBg: "rgba(43, 74, 203, 0.12)",
                  itemSelectedColor: "#2B4ACB",
                  itemSelectedIconColor: "#2B4ACB",
                },
          },
        }}
      >
        <div className={isDark ? "app-dark" : "app-light"}>
          <Refine
            options={{
              disableTelemetry: true,
            }}
            // disableTelemetry={true}
            dataProvider={dataProvider}
            // notificationProvider={antdNotificationProvider}
            notificationProvider={notistackNotificationProvider}
            // keep refine aware of CRUD resources only
            resources={leaves
              .filter((l) => l.resource)
              .map((l) => ({
                name: l.resource!,
                list: l.path,
              }))}
          >
            <AppLayout
              isDark={isDark}
              onToggleTheme={() => setIsDark((v) => !v)}
            >
              <Routes>
                <Route path="/wiki/*" element={<WikiControl />} />
                {leaves.map((leaf) => {
                  // standalone page?
                  if (leaf.componentKey) {
                    const Comp = PAGE_COMPONENTS[leaf.componentKey];
                    return (
                      <Route
                        key={leaf.path}
                        path={leaf.path}
                        element={Comp ?? <div>Page not found</div>}
                      />
                    );
                  }

                  // CRUD page
                  if (leaf.resource) {
                    return (
                      <Route
                        key={leaf.path}
                        path={leaf.path}
                        element={<GenericCrud resource={leaf.resource} />}
                      />
                    );
                  }

                  return null;
                })}

                {/* fallback to first leaf */}
                {leaves[0] ? (
                  <Route
                    path="*"
                    element={
                      leaves[0].componentKey ? (
                        (PAGE_COMPONENTS[leaves[0].componentKey] ?? (
                          <div>Page</div>
                        ))
                      ) : leaves[0].resource ? (
                        <GenericCrud resource={leaves[0].resource} />
                      ) : (
                        <div>Not found</div>
                      )
                    }
                  />
                ) : null}
              </Routes>
            </AppLayout>
          </Refine>
        </div>
      </ConfigProvider>
    </BrowserRouter>
  );
};
